import { ReactElement, useState, useMemo, useEffect } from 'react';
import { useRuns, useInvalidateRuns } from '../../hooks/useRuns';
import { useSessionsWithConfigs } from '../../hooks/useSessions';
import SectionHeader from '../../components/SectionHeader';
import { FormProvider, useForm } from 'react-hook-form';
import { createColumnHelper } from '@tanstack/react-table';
import { configShortLabel } from 'utils/configRole';
import RunEditMenu from './RunEntry/RunEditMenu';
import { useNavigate, useLocation } from 'react-router-dom';
import Table from '../../components/Table';
import HealthDot from '../../components/HealthDot';
import { SessionAnalysisTypeUtils } from '../../types/SessionAnalysisType';
import { getStageName } from '../../types/Run';
import { updateRunsBulk } from '../../api/runs';
import { runTimestampLabel } from '../../utils/sessionTime';
import { useWatchSettlingRuns } from '../../hooks/useWatchSettlingRuns';
import toast from 'react-hot-toast';

interface Run {
  id: string;
  config_id?: string;
  rider_id?: string;
  run_ts?: string | null;
  run_status?: number | null;
  timezone?: string | null;
  run_health?: number | null;
  run_health_reasons?: string[] | null;
  health_code?: number | null;
  [key: string]: any;
}

interface MoveToSessionState {
  moveToSessionId?: string;
  moveToConfigId?: string;
  moveToSessionDesc?: string;
}

export default function RunList(): ReactElement {
  const formMethods = useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const moveState = location.state as MoveToSessionState | null;

  const [analysisTypeFilter, setAnalysisTypeFilter] = useState<string>(
    () => {
      if (moveState?.moveToConfigId) return 'unallocated';
      return localStorage.getItem('runListFilter') || 'valid';
    },
  );
  const [selectedRunIds, setSelectedRunIds] = useState<Set<string>>(
    new Set(),
  );
  const [isMoving, setIsMoving] = useState(false);

  useEffect(() => {
    localStorage.setItem('runListFilter', analysisTypeFilter);
  }, [analysisTypeFilter]);
  const queryString =
    'scope=with_config_minimal&order=DESC&order_by=run_ts';
  const invalidateRuns = useInvalidateRuns(queryString);

  const buttonStyle =
    'cr-border-none cr-rounded-full cr-bg-transparent cr-text-cirrusBlue-100 hover:cr-bg-cirrusBlue-25 cr-shadow-none cr-transition-colors cr-duration-200 cr-cursor-pointer';

  const { isFetching: isRunsFetching, isPending: isRunsPending, data: runs } = useRuns(queryString);
  const { data: sessionsWithConfigs, isPending: isSessionsPending } = useSessionsWithConfigs();

  // Filter runs based on their session's analysis_type
  const filteredRuns = useMemo(() => {
    if (!runs) return runs;

    return runs.filter((run: any) => {
      const session = run.config?.session;
      const analysisType = session?.analysis_type;

      if (analysisTypeFilter === 'unallocated') {
        return !run.config_id || !session
          || SessionAnalysisTypeUtils.isDiscarded(analysisType);
      }

      if (!session) return analysisTypeFilter === 'all';

      switch (analysisTypeFilter) {
        case 'valid':
          return SessionAnalysisTypeUtils.isValid(analysisType);
        case 'session':
          return SessionAnalysisTypeUtils.isSession(analysisType);
        case 'freeRide':
          return SessionAnalysisTypeUtils.isFreeRide(analysisType);
        case 'discarded':
          return SessionAnalysisTypeUtils.isDiscarded(analysisType);
        case 'hidden':
          return SessionAnalysisTypeUtils.isHidden(analysisType);
        case 'all':
        default:
          return true;
      }
    });
  }, [runs, analysisTypeFilter]);

  useEffect(() => {
    if (filteredRuns && filteredRuns.length > 0) {
      const ids = filteredRuns.map((r: Run) => r.id);
      localStorage.setItem('runListFilteredIds', JSON.stringify(ids));
    }
  }, [filteredRuns]);

  // Per-run progress for anything still settling; the provider also invalidates
  // ['runs'] when one lands, which is what refreshes this list.
  useWatchSettlingRuns(filteredRuns);

  const isMoveMode = !!(moveState?.moveToConfigId);

  const toggleRunSelection = (runId: string) => {
    setSelectedRunIds((prev) => {
      const next = new Set(prev);
      if (next.has(runId)) next.delete(runId);
      else next.add(runId);
      return next;
    });
  };

  const handleMoveRuns = async () => {
    if (!moveState?.moveToConfigId || selectedRunIds.size === 0) return;
    setIsMoving(true);
    try {
      const payload = Array.from(selectedRunIds).map((id) => ({
        id,
        config_id: moveState.moveToConfigId,
      }));
      await updateRunsBulk(payload);
      invalidateRuns();
      toast.success(
        `Moved ${payload.length} run(s) to session`,
      );
      if (moveState?.moveToSessionId) {
        navigate(
          `/sessions/${moveState.moveToSessionId}`,
        );
      }
    } catch (error) {
      console.error('Error moving runs:', error);
      toast.error('Error moving runs to session');
    } finally {
      setIsMoving(false);
    }
  };

  const handleRowClick = (e: React.MouseEvent, row: Run) => {
    if (isMoveMode) {
      toggleRunSelection(row.id);
      return;
    }
    navigate(`/runs/${row.id}`);
  };

  const columnHelper = useMemo(() => createColumnHelper<Run>(), []);
  const tableColumns = useMemo(() => [
    ...(isMoveMode
      ? [
        columnHelper.display({
          id: 'select',
          header: '',
          cell: ({ row: tableRow }) => (
            <input
              type="checkbox"
              checked={selectedRunIds.has(tableRow.original.id)}
              onChange={() => toggleRunSelection(
                tableRow.original.id,
              )}
              onClick={(e) => e.stopPropagation()}
              className="cr-cursor-pointer"
              style={{
                accentColor: '#03365D',
                width: '18px',
                height: '18px',
              }}
            />
          ),
          enableSorting: false,
        }),
      ]
      : []),
    // display(), not accessor(): the menu is rendered per visible cell, not
    // built for every row on each row-model rebuild.
    columnHelper.display({
      id: 'options',
      header: '',
      cell: ({ row: tableRow }) => (
        <RunEditMenu
          run={tableRow.original}
          config={tableRow.original.config}
          session={tableRow.original.config?.session}
          sessions={sessionsWithConfigs}
          sessionsLoading={isSessionsPending}
          invalidateRuns={invalidateRuns}
          buttonStyle={buttonStyle}
          moveRight={true}
        />
      ),
      enableSorting: false,
    }),
    columnHelper.accessor((row) => row.run_health, {
      id: 'health',
      header: 'Health',
      cell: (info) => (
        <HealthDot
          health={info.row.original.run_health}
          reasons={info.row.original.run_health_reasons}
        />
      ),
      sortingFn: (a, b) => (a.original.run_health ?? -1) - (b.original.run_health ?? -1),
      enableSorting: true,
    }),
    columnHelper.accessor('health_code', {
      id: 'stage',
      header: 'Stage',
      cell: (info) => {
        const code = info.getValue();
        return <span title={`Code: ${code}`}>{getStageName(code)}</span>;
      },
      enableSorting: true,
    }),
    columnHelper.accessor('run_number', {
      header: 'Run #',
      cell: (info) => info.getValue(),
      enableSorting: true,
    }),
    columnHelper.accessor((row) => row.run_ts, {
      id: 'time',
      header: 'Time',
      cell: (info) => runTimestampLabel(info.row.original, 'datetime'),
      enableSorting: true,
    }),
    columnHelper.accessor((row) => row.config?.session?.riders?.nickname, {
      id: 'rider',
      header: 'Rider',
      cell: (info) => info.getValue() || '--',
      enableSorting: true,
    }),
    columnHelper.accessor(
      (row) => {
        const history = row.history;
        if (Array.isArray(history) && history.length > 0) {
          const latest = history[history.length - 1];
          if (latest?.description) return latest.description;
        }
        return row.description || '';
      },
      {
        id: 'description',
        header: 'Description',
        cell: (info) => info.getValue() || '',
        enableSorting: true,
      },
    ),
    columnHelper.accessor((row) => row.config?.session?.description, {
      id: 'session',
      header: 'Session',
      cell: (info) => info.getValue() || '--',
      enableSorting: true,
    }),
    columnHelper.accessor((row) => row.config?.letter_number, {
      id: 'config',
      header: 'Cfg',
      cell: (info) => {
        return configShortLabel(info.getValue());
      },
      enableSorting: true,
    }),
  ], [
    columnHelper, isMoveMode, selectedRunIds, sessionsWithConfigs,
    isSessionsPending, invalidateRuns,
  ]);

  return (
    <FormProvider {...formMethods}>
      <div className="cr-w-full">
        <SectionHeader title="Runs" isLoading={isRunsFetching} />

        {/* Analysis Type Filter Radio Buttons */}
        <div className="cr-mb-4 cr-flex cr-gap-4 cr-rounded-lg cr-bg-white cr-p-4 cr-shadow-md">
          <label className="cr-flex cr-cursor-pointer cr-items-center cr-gap-2">
            <input
              type="radio"
              name="analysisTypeFilter"
              value="valid"
              checked={analysisTypeFilter === 'valid'}
              onChange={(e) => setAnalysisTypeFilter(e.target.value)}
              className="cr-cursor-pointer"
              style={{ accentColor: '#03365D', width: '18px', height: '18px' }}
            />
            <span className="cr-text-base cr-font-medium">Valid</span>
          </label>

          <label className="cr-flex cr-cursor-pointer cr-items-center cr-gap-2">
            <input
              type="radio"
              name="analysisTypeFilter"
              value="session"
              checked={analysisTypeFilter === 'session'}
              onChange={(e) => setAnalysisTypeFilter(e.target.value)}
              className="cr-cursor-pointer"
              style={{ accentColor: '#03365D', width: '18px', height: '18px' }}
            />
            <span className="cr-text-base cr-font-medium">Session</span>
          </label>

          <label className="cr-flex cr-cursor-pointer cr-items-center cr-gap-2">
            <input
              type="radio"
              name="analysisTypeFilter"
              value="freeRide"
              checked={analysisTypeFilter === 'freeRide'}
              onChange={(e) => setAnalysisTypeFilter(e.target.value)}
              className="cr-cursor-pointer"
              style={{ accentColor: '#03365D', width: '18px', height: '18px' }}
            />
            <span className="cr-text-base cr-font-medium">Free Ride</span>
          </label>

          <label className="cr-flex cr-cursor-pointer cr-items-center cr-gap-2">
            <input
              type="radio"
              name="analysisTypeFilter"
              value="discarded"
              checked={analysisTypeFilter === 'discarded'}
              onChange={(e) => setAnalysisTypeFilter(e.target.value)}
              className="cr-cursor-pointer"
              style={{ accentColor: '#03365D', width: '18px', height: '18px' }}
            />
            <span className="cr-text-base cr-font-medium">Discarded</span>
          </label>

          <label className="cr-flex cr-cursor-pointer cr-items-center cr-gap-2">
            <input
              type="radio"
              name="analysisTypeFilter"
              value="unallocated"
              checked={analysisTypeFilter === 'unallocated'}
              onChange={(e) => setAnalysisTypeFilter(e.target.value)}
              className="cr-cursor-pointer"
              style={{
                accentColor: '#03365D',
                width: '18px',
                height: '18px',
              }}
            />
            <span className="cr-text-base cr-font-medium">
              Unallocated
            </span>
          </label>

          <label className="cr-flex cr-cursor-pointer cr-items-center cr-gap-2">
            <input
              type="radio"
              name="analysisTypeFilter"
              value="hidden"
              checked={analysisTypeFilter === 'hidden'}
              onChange={(e) => setAnalysisTypeFilter(e.target.value)}
              className="cr-cursor-pointer"
              style={{
                accentColor: '#03365D',
                width: '18px',
                height: '18px',
              }}
            />
            <span className="cr-text-base cr-font-medium">Hidden</span>
          </label>

          <label className="cr-flex cr-cursor-pointer cr-items-center cr-gap-2">
            <input
              type="radio"
              name="analysisTypeFilter"
              value="all"
              checked={analysisTypeFilter === 'all'}
              onChange={(e) => setAnalysisTypeFilter(e.target.value)}
              className="cr-cursor-pointer"
              style={{ accentColor: '#03365D', width: '18px', height: '18px' }}
            />
            <span className="cr-text-base cr-font-medium">All</span>
          </label>
        </div>

        {isMoveMode && (
          <div className={
            'cr-mb-4 cr-flex cr-items-center cr-gap-4 '
            + 'cr-rounded-lg cr-bg-cirrusBlue-25 cr-p-4 cr-shadow-md'
          }>
            <span className="cr-text-sm cr-font-medium cr-text-blue-900">
              Moving to: {moveState?.moveToSessionDesc || 'Session'}
            </span>
            <button
              onClick={handleMoveRuns}
              disabled={selectedRunIds.size === 0 || isMoving}
              className={
                'cr-rounded-lg cr-bg-blue-900 cr-px-4 cr-py-2 '
                + 'cr-text-sm cr-font-medium cr-text-white '
                + 'cr-transition-colors hover:cr-bg-blue-600 '
                + 'disabled:cr-cursor-not-allowed disabled:cr-opacity-40'
              }
            >
              {isMoving
                ? 'Moving...'
                : `Move${selectedRunIds.size > 0
                  ? ` (${selectedRunIds.size})`
                  : ''}`}
            </button>
            <button
              onClick={() => navigate('/runs', { replace: true })}
              className={
                'cr-rounded-lg cr-border cr-border-gray-300 cr-px-4 '
                + 'cr-py-2 cr-text-sm cr-font-medium cr-text-gray-700 '
                + 'cr-transition-colors hover:cr-bg-gray-100'
              }
            >
              Cancel
            </button>
          </div>
        )}

        <Table
          data={filteredRuns || []}
          columns={tableColumns}
          onRowClick={handleRowClick}
          isLoading={isRunsPending}
          getRowId={(row) => row.id}
        />
      </div>
    </FormProvider>
  );
}
